import { useEffect, useRef } from 'react';
import { loadChatDraft, saveChatDraft } from '@/lib/chatDrafts';
import { settingsStore } from '@/lib/settingsStore';

// ============================================================================
// useChatDraft — ЧЕРНОВИК сообщения ТЕКУЩЕГО чата (единый механизм для
// ChatPage, контекстных обсуждений и виджетов чата):
//   - при открытии чата черновик восстанавливается в поле ввода (загрузка
//     ждёт гидрации настроек; уже начатый ввод пользователя НЕ затирается);
//   - при вводе текст сохраняется с задержкой 500 мс;
//   - при переключении чата и уходе (размонтирование) — сохранение немедленно;
//   - сохраняется ТОЛЬКО текст: вложения и ответ (цитата) не сохраняются.
// ============================================================================

/** Задержка сохранения черновика при вводе (мс). */
export const CHAT_DRAFT_DEBOUNCE_MS = 500;

export function useChatDraft(conversationId: string, text: string, setText: (value: string) => void): void {
  // ЗНАЧЕНИЕ ПОЛЯ последнего рендера (по нему видно «пользователь начал ввод»)
  const latestTextRef = useRef(text);
  latestTextRef.current = text;
  // ЗНАЧЕНИЕ, ПЕРЕДАННОЕ в поле последним: между setText() и следующим рендером
  // оно опережает состояние — при уходе сохраняем именно его (иначе уход в
  // первом коммите затёр бы только что загруженный черновик).
  const intendedTextRef = useRef(text);
  intendedTextRef.current = text;

  // Восстановление черновика при открытии чата.
  useEffect(() => {
    const convId = conversationId;
    const apply = (draft: string) => {
      intendedTextRef.current = draft;
      setText(draft);
    };
    if (settingsStore.isHydrated()) {
      apply(loadChatDraft(convId));
      return;
    }
    // Гидрация ещё идёт: ждём её ОДИН раз (подписка снимается сразу).
    let finished = false;
    const unsubscribe = settingsStore.subscribe(() => {
      if (finished || !settingsStore.isHydrated()) return;
      finished = true;
      unsubscribe();
      if (latestTextRef.current !== '') return; // пользователь уже начал ввод — не затираем
      const draft = loadChatDraft(convId);
      if (draft !== '') apply(draft);
    });
    return () => {
      finished = true;
      unsubscribe();
    };
  }, [conversationId, setText]);

  // Сохранение при вводе (задержка 500 мс; пустой текст — удаление черновика).
  useEffect(() => {
    const timer = setTimeout(() => saveChatDraft(conversationId, text), CHAT_DRAFT_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [conversationId, text]);

  // Переключение чата / уход — сохранение немедленно, последним переданным
  // в поле значением (пустое — черновик удаляется).
  useEffect(() => {
    const convId = conversationId;
    return () => {
      saveChatDraft(convId, intendedTextRef.current);
    };
  }, [conversationId]);
}
